/**
 * Finance → Control Presupuestario → Company Metrics: modelo puro (sin red ni
 * React). Tiene tres partes:
 *
 *  1. Series: de 12 meses a trimestres + FY con la regla de cada métrica
 *     ('sum' para flujos, 'last' para stocks, 'mean' para ratios mensuales) o
 *     como cociente de sumas (`ratioSeries`).
 *  2. Overlays en vivo: arman las filas del reporte desde BigQuery con los
 *     mismos nombres que la planilla CM 2.0, para pisar el valor fijo:
 *       - P&L y Financial Performance 2026 desde el dataset del P&L
 *         (`PnlDashboardData`, el mismo de la pestaña P&L).
 *       - Revenue Performance 2026 (target = presupuesto, attainment, MoM/YoY).
 *       - Flywheel (Capacity_Process.Flywheel_Client_KPIs + NDR_Holding_Monthly).
 *       - People 2026 (P_L.headcount + revenue Actual de Capacity_Final).
 *     Validado contra el HTML de Finance (07/10/2026): Flywheel 19 métricas
 *     exactas en 32 meses, NDR exacto, headcount exacto, Ebitda - Plan exacto.
 *  3. Navegación y árbol del P&L (secciones, bloques, padres e hijos).
 *
 * Tests: `npm run test:company-metrics` (backend).
 */

import { ebitdaFinalOf, ebitdaOf, grossProfitOf, opexOf } from './finance-pnl';
import type { PnlDashboardData, PnlMonthLines } from './types/finance-pnl';
import type { CmFormat, CmRow, CmSectionKey, CmSeries } from './types/company-metrics';

/* ───────────────────────── series ───────────────────────── */

export type CmAgg = 'sum' | 'last' | 'mean';

const QUARTERS = [[0, 1, 2], [3, 4, 5], [6, 7, 8], [9, 10, 11]];

function agg(vals: Array<number | null>, how: CmAgg): number | null {
  const v = vals.filter((x): x is number => x !== null && Number.isFinite(x));
  if (!v.length) return null;
  if (how === 'last') return v[v.length - 1];
  const s = v.reduce((a, b) => a + b, 0);
  return how === 'sum' ? s : s / v.length;
}

/** 12 meses (null = sin dato) → serie con trimestres y FY según la regla. */
export function seriesFrom(months: Array<number | null>, how: CmAgg): CmSeries {
  const m = Array.from({ length: 12 }, (_, i) => months[i] ?? null);
  return { months: m, quarters: QUARTERS.map((q) => agg(q.map((i) => m[i]), how)), fy: agg(m, how) };
}

/**
 * Cociente mes a mes; trimestre y FY = suma del numerador / suma del denominador
 * en los meses con numerador. `denAll`: el denominador suma TODO el período
 * (attainment contra el target del trimestre / año completo, como la planilla); si a ese
 * período le falta un mes de denominador (p. ej. un año con Plan pero sin Plan H2) el cociente
 * es null: sumar 12 meses de revenue sobre 6 de target inflaba el attainment (code-reviewer 16/10/2026).
 */
export function ratioSeries(num: Array<number | null>, den: Array<number | null>, opts: { denAll?: boolean } = {}): CmSeries {
  const at = (i: number) => (num[i] != null && den[i] ? (num[i] as number) / (den[i] as number) : null);
  const over = (idx: number[]) => {
    const withNum = idx.filter((i) => num[i] != null);
    if (!withNum.length) return null;
    if (opts.denAll && idx.some((i) => den[i] == null)) return null;
    const n = withNum.reduce((a, i) => a + (num[i] as number), 0);
    const d = (opts.denAll ? idx : withNum).reduce((a, i) => a + (den[i] ?? 0), 0);
    return d ? n / d : null;
  };
  return {
    months: Array.from({ length: 12 }, (_, i) => at(i)),
    quarters: QUARTERS.map(over),
    fy: over(Array.from({ length: 12 }, (_, i) => i)),
  };
}

/**
 * Crecimiento contra la serie anterior: (a / b) − 1 por mes, trimestre (sumas) y FY (si `fy`).
 * El trimestre solo se compara si `cur` tiene sus 3 meses: un trimestre a medio cerrar
 * contra uno completo daría una caída falsa (code-reviewer 07/10/2026).
 */
export function growthSeries(cur: CmSeries, prev: CmSeries, opts: { fy?: boolean } = {}): CmSeries {
  const g = (a: number | null, b: number | null) => (a != null && b ? a / b - 1 : null);
  const complete = (q: number) => QUARTERS[q].every((i) => cur.months[i] != null);
  return {
    months: cur.months.map((a, i) => g(a, prev.months[i])),
    quarters: cur.quarters.map((a, i) => (complete(i) ? g(a, prev.quarters[i]) : null)),
    fy: opts.fy ? g(cur.fy, prev.fy) : null,
  };
}

/* ───────────────────────── formato ───────────────────────── */

/** Métricas que se muestran como % (la planilla las guarda como fracción). */
export const CM_PCT_METRICS = new Set([
  'Gross Margin %', 'Investment as % Revenue', 'G&A as % Revenue', 'EBITDA as % Revenue',
  'Net Revenue MoM Growth', 'Net Revenue YoY Growth', 'Attainment vs Target', 'Attainment vs Plan', 'Opex MoM Growth',
  'Revenue churn (%)', 'Churns/Active (Logos)', 'Clients Annual Retention %', 'Top Client %', 'Top 5 (%)', 'Top 10 (%)',
  '% Holdings con >1 Solution', 'Attainment vs Plan (New Business)', 'Net Dollar Retention', 'Net Dollar Retention - Still Active',
  'Budget: Net Dollar Retention', 'Net Dollar Retention exc. Partnerships', 'Net Dollar Retention - Still Active exc. Partnerships',
  'Share% of total Revenue', 'Gross Margin as % Revenue', 'Share% Rebates', 'NPS promoter % of answers (Q)', 'NPS - % Of clients Satisfied',
  '% Direct Cost / Revenue', '% Indirect Cost / Revenue', '% Taxes / Revenue', '% Investment / Revenue',
  '% G&A / Revenue', '% EBITDA / Revenue', 'Gross Margin Total w/o Taxes', 'GM w/o taxes',
  'Gross Margin Paid Media Lab - Direct only', 'Gross Margin Paid Media Lab - Direct+Indirect',
  'Gross Margin Content - Direct only', 'Gross Margin Content - Direct+Indirect',
  'Gross Margin Owned Media Lab - Direct only', 'Gross Margin Owned Media Lab - Direct+Indirect',
  'Gross Margin Tech & Data Studio - Direct only', 'Gross Margin Tech & Data Studio - Direct+Indirect',
  'Weight Indirect Cost / Revenue - Paid Media Lab', 'Weight Indirect Cost / Revenue - Content',
  'Weight Indirect Cost / Revenue - Owned Media Lab', 'Weight Indirect Cost / Revenue - Tech & Data Studio',
  'Weight Direct Cost / Revenue - Paid Media Lab', 'Weight Direct Cost / Revenue - Content',
  'Weight Direct Cost / Revenue - Owned Media Lab', 'Weight Direct Cost / Revenue - Tech & Data Studio',
]);

/** Filas resaltadas del reporte. */
export const CM_EMPH = new Set(['Net Revenue', 'EBITDA', 'EBITDA Adj', 'Gross Margin', 'Revenue', 'Gross Profit', 'EBITDA post Bonuses']);

export const formatOf = (metric: string): CmFormat => (CM_PCT_METRICS.has(metric) ? 'pct' : 'num');

/* ───────────────────────── navegación ───────────────────────── */

export const CM_SECTIONS: ReadonlyArray<{ key: CmSectionKey; subs: ReadonlyArray<{ key: string; label: string }> }> = [
  {
    key: 'company',
    subs: [
      { key: 'financial', label: 'Financial Performance' },
      { key: 'revenue', label: 'Revenue Performance' },
      { key: 'clients', label: 'Flywheel: Clients' },
      { key: 'people', label: 'People' },
    ],
  },
  {
    key: 'practices',
    subs: [
      { key: 'pml', label: 'Paid Media Lab' },
      { key: 'content', label: 'Content' },
      { key: 'oml', label: 'Owned Media Lab' },
      { key: 'tds', label: 'Tech & Data Studio' },
      { key: 'dfc', label: 'Data for Content' },
    ],
  },
  {
    key: 'segment',
    subs: [
      { key: 'rola', label: 'ROLA' },
      { key: 'whoppers', label: 'Whoppers' },
      { key: 'growth_transformation', label: 'Growth Transformation' },
      { key: 'andinos', label: 'Andinos' },
      { key: 'mexico', label: 'Mexico' },
      { key: 'usa', label: 'US' },
      { key: 'brazil', label: 'Brazil' },
      { key: 'partnerships_content', label: 'Partnerships Content' },
    ],
  },
  {
    key: 'pl',
    subs: [
      { key: 'main', label: 'P&L' },
      { key: 'ratios', label: 'Ratios & Checks' },
    ],
  },
];

/** Bloques del P&L por sub-pestaña (los hijos "X - Y" cuelgan de su padre y se pliegan). */
export const CM_PL_BLOCKS: Record<'main' | 'ratios', ReadonlyArray<{ title: string; metrics: string[] }>> = {
  main: [
    { title: 'Revenue & Cogs', metrics: ['Revenue', 'Taxes', 'Cogs w/o Taxes', 'Direct Cost', 'Indirect Cost', 'Gross Profit', 'Gross Margin %'] },
    { title: 'Investment & G&A', metrics: ['Investment', 'G&A'] },
    { title: 'EBITDA & Resultado', metrics: ['EBITDA w/o annual bonuses', 'Bonos Anuales', 'EBITDA post Bonuses', 'Other incomes', 'Existencial (below EBITDA)', 'Other Expenses', 'EBT', 'Net Income', 'Interest expense', 'Result after Interest'] },
  ],
  ratios: [
    {
      title: 'Checks',
      metrics: ['Opex total (Bonos anuales, taxes, etc)', 'Opex Total Sin Bono ni Taxes', 'Opex Total con Bono sin Taxes', 'Cogs con Taxes', 'Payroll', 'Non Payroll', 'Others (Annual bonus)', 'Indemnizaciones', 'Opex sin Indemnizaciones', 'Control de Base OPEX sin taxes', 'Diferencia', 'Existencial (G&A)', 'Non Payroll Excl Existencial', 'GM w/o taxes'],
    },
    { title: 'Ratios / KPIs (% sobre Revenue)', metrics: ['% Direct Cost / Revenue', '% Indirect Cost / Revenue', '% Taxes / Revenue', '% Investment / Revenue', '% G&A / Revenue', '% EBITDA / Revenue'] },
    {
      title: 'Gross Margin por solución',
      metrics: ['Gross Margin Paid Media Lab - Direct only', 'Gross Margin Paid Media Lab - Direct+Indirect', 'Gross Margin Content - Direct only', 'Gross Margin Content - Direct+Indirect', 'Gross Margin Owned Media Lab - Direct only', 'Gross Margin Owned Media Lab - Direct+Indirect', 'Gross Margin Tech & Data Studio - Direct only', 'Gross Margin Tech & Data Studio - Direct+Indirect', 'Gross Margin Total w/o Taxes'],
    },
    {
      title: 'Indirects por solución',
      metrics: ['Indirect Cost Total - Paid Media Lab', 'Weight Indirect Cost / Revenue - Paid Media Lab', 'Indirect Cost Total - Content', 'Weight Indirect Cost / Revenue - Content', 'Indirect Cost Total - Owned Media Lab', 'Weight Indirect Cost / Revenue - Owned Media Lab', 'Indirect Cost Total - Tech & Data Studio', 'Weight Indirect Cost / Revenue - Tech & Data Studio'],
    },
    { title: 'Weight over Revenue (Direct Cost)', metrics: ['Weight Direct Cost / Revenue - Paid Media Lab', 'Weight Direct Cost / Revenue - Content', 'Weight Direct Cost / Revenue - Owned Media Lab', 'Weight Direct Cost / Revenue - Tech & Data Studio'] },
  ],
};

/** Padres explícitos (Investment → Same Store / New Business) y prefijos que cuelgan de ellos. */
const EXTRA_CHILDREN: Record<string, string[]> = { Investment: ['Investment Same Store', 'Investment New Business'] };
const EXTRA_PARENT: Record<string, string> = { 'Same Store - ': 'Investment Same Store', 'New Business - ': 'Investment New Business' };
/** Filas "X - Y" de los bloques de ratios que NO son hijas (son métricas sueltas con guión en el nombre). */
const STANDALONE = new Set(Object.values(CM_PL_BLOCKS).flatMap((bs) => bs.flatMap((b) => b.metrics)));

export interface CmTree {
  childrenOf: Record<string, string[]>;
  parentOf: Record<string, string>;
}

/** Árbol del P&L: "Direct Cost - Content" cuelga de "Direct Cost"; "Same Store - X" de "Investment Same Store". */
export function buildPlTree(rows: ReadonlyArray<{ metric: string }>): CmTree {
  const names = new Set(rows.map((r) => r.metric));
  const childrenOf: Record<string, string[]> = {};
  const parentOf: Record<string, string> = {};
  const link = (child: string, parent: string) => {
    parentOf[child] = parent;
    (childrenOf[parent] ??= []).push(child);
  };
  for (const r of rows) {
    if (STANDALONE.has(r.metric) || EXTRA_CHILDREN[r.metric]) continue;
    const i = r.metric.indexOf(' - ');
    if (i < 0) continue;
    const prefix = r.metric.slice(0, i + 3);
    const parent = EXTRA_PARENT[prefix] ?? r.metric.slice(0, i);
    if (names.has(parent)) link(r.metric, parent);
  }
  for (const [parent, kids] of Object.entries(EXTRA_CHILDREN)) for (const k of kids) if (names.has(k) && names.has(parent)) link(k, parent);
  return { childrenOf, parentOf };
}

/** Lo que se muestra de un hijo: "Direct Cost - Content" → "Content". */
export function childLabel(metric: string): string {
  const i = metric.indexOf(' - ');
  return i < 0 ? metric : metric.slice(i + 3);
}

/* ───────────────────────── overlays: P&L ───────────────────────── */

const K = (usd: number) => usd / 1000;

/** Los 12 meses de un año del P&L (null = mes sin cierre). */
function monthly(data: PnlDashboardData, f: (l: PnlMonthLines, month: number) => number): Array<number | null> {
  const byMonth = new Map(data.actual.map((l) => [l.month, l]));
  return Array.from({ length: 12 }, (_, i) => {
    const l = byMonth.get(i + 1);
    return l && i + 1 <= data.closedMonth ? K(f(l, i + 1)) : null;
  });
}

function sumBy<T extends { month: number }>(rows: T[], closed: number, key: (r: T) => string | null, usd: (r: T) => number): Map<string, Array<number | null>> {
  const out = new Map<string, Array<number | null>>();
  for (const r of rows) {
    const k = key(r);
    if (!k || r.month < 1 || r.month > closed) continue;
    const arr = out.get(k) ?? Array.from({ length: 12 }, (_, i) => (i + 1 <= closed ? 0 : null));
    arr[r.month - 1] = (arr[r.month - 1] ?? 0) + K(usd(r));
    out.set(k, arr);
  }
  return out;
}

const add = (...xs: Array<Array<number | null>>) => Array.from({ length: 12 }, (_, i) => (xs.every((x) => x[i] == null) ? null : xs.reduce((a, x) => a + (x[i] ?? 0), 0)));
const sub = (a: Array<number | null>, b: Array<number | null>) => a.map((v, i) => (v == null ? null : v - (b[i] ?? 0)));
const zeros = (closed: number) => Array.from({ length: 12 }, (_, i) => (i + 1 <= closed ? 0 : null));

/** Las soluciones con margen por solución en "Ratios & Checks". */
const GM_SOLUTIONS = ['Paid Media Lab', 'Content', 'Owned Media Lab', 'Tech & Data Studio'];

/**
 * Familias de filas que el overlay del P&L cubre: si una fila de la planilla es de
 * una de estas familias y no sale en vivo (p. ej. un centro de costo que este año
 * no gastó), su 2026 queda vacío en vez de mostrar el número viejo de la planilla.
 */
/** Las líneas cuyo payroll es "Payroll" en el CM (Finance, 08/10/2026). */
const PAYROLL_LINES: readonly string[] = ['cogs', 'gya', 'investment'];

export const PL_LIVE_FAMILIES = ['Revenue - ', 'Direct Cost - ', 'Indirect Cost - ', 'Same Store - ', 'New Business - ', 'G&A - '];

/** Filas del P&L (sección P&L) para el año del dataset, en miles de USD, con los nombres de la planilla. */
export function plRowsFromPnl(data: PnlDashboardData): Map<string, CmSeries> {
  const c = data.closedMonth;
  const out = new Map<string, CmSeries>();
  const put = (name: string, months: Array<number | null>, how: CmAgg = 'sum') => out.set(name, seriesFrom(months, how));
  const ratio = (name: string, num: Array<number | null>, den: Array<number | null>) => out.set(name, ratioSeries(num, den));

  const rev = monthly(data, (l) => l.revenue);
  const taxes = monthly(data, (l) => l.taxes);
  const cogs = monthly(data, (l) => l.cogs);
  const inv = monthly(data, (l) => l.investment);
  const gya = monthly(data, (l) => l.gya);
  const bonos = monthly(data, (l) => l.bonos);
  const gp = monthly(data, (l) => grossProfitOf(l));
  const ebitda = monthly(data, (l) => ebitdaOf(l));
  const ebitdaPre = add(ebitda, bonos);
  const belowBy = new Map(data.below.map((b) => [b.month, b]));
  const below = (f: (b: { otherIncomes: number; existencial: number; otherExpenses: number }) => number) =>
    Array.from({ length: 12 }, (_, i) => (i + 1 <= c ? K(f(belowBy.get(i + 1) ?? { otherIncomes: 0, existencial: 0, otherExpenses: 0 })) : null));
  const ebt = monthly(data, (l, m) => ebitdaFinalOf(l, belowBy.get(m) ?? { otherIncomes: 0, existencial: 0, otherExpenses: 0 }));
  const extraBy = new Map((data.extra ?? []).map((e) => [e.month, e]));
  const interest = Array.from({ length: 12 }, (_, i) => (i + 1 <= c ? K(extraBy.get(i + 1)?.interestExpense ?? 0) : null));

  const actualOpex = data.opex.filter((r) => r.scenario === 'actual');
  const revBySolution = sumBy(data.revenue.filter((r) => r.scenario === 'actual'), c, (r) => r.solution, (r) => r.usd);
  const cogsBy = (group: string) => sumBy(actualOpex.filter((r) => r.line === 'cogs' && r.group === group), c, (r) => r.costCenter, (r) => r.usd);
  const direct = cogsBy('Direct Cost');
  const indirect = cogsBy('Indirect Cost');
  const total = (m: Map<string, Array<number | null>>) => (m.size ? add(...m.values()) : zeros(c));
  const directT = total(direct);
  const indirectT = total(indirect);
  const invBy = (group: string) => sumBy(actualOpex.filter((r) => r.line === 'investment' && r.group === group), c, (r) => r.costCenter, (r) => r.usd);
  const ss = invBy('Same Store');
  const nb = invBy('New Business');
  const gyaBy = sumBy(actualOpex.filter((r) => r.line === 'gya'), c, (r) => r.group, (r) => r.usd);
  const nonPayroll = total(sumBy(actualOpex.filter((r) => r.kind === 'nonPayroll'), c, () => 'x', (r) => r.usd));
  const existencialGya = gyaBy.get('Existencial') ?? zeros(c);

  // ── Revenue & Cogs
  put('Revenue', rev);
  for (const [s, v] of revBySolution) put(`Revenue - ${s}`, v);
  put('Taxes', taxes);
  put('Cogs w/o Taxes', cogs);
  put('Direct Cost', directT);
  for (const [cc, v] of direct) put(`Direct Cost - ${cc}`, v);
  put('Indirect Cost', indirectT);
  for (const [cc, v] of indirect) put(`Indirect Cost - ${cc}`, v);
  put('Gross Profit', gp);
  ratio('Gross Margin %', gp, rev);
  // ── Investment & G&A
  put('Investment', inv);
  put('Investment Same Store', total(ss));
  for (const [cc, v] of ss) put(`Same Store - ${cc}`, v);
  put('Investment New Business', total(nb));
  for (const [cc, v] of nb) put(`New Business - ${cc}`, v);
  put('G&A', gya);
  for (const [g, v] of gyaBy) put(`G&A - ${g}`, v);
  // ── EBITDA & Resultado
  put('EBITDA w/o annual bonuses', ebitdaPre);
  put('Bonos Anuales', bonos);
  put('EBITDA post Bonuses', ebitda);
  put('Other incomes', below((b) => b.otherIncomes));
  put('Existencial (below EBITDA)', below((b) => b.existencial));
  put('Other Expenses', below((b) => b.otherExpenses));
  // EBT y Net Income = EBITDA Final, como en la planilla (no hay D&A ni impuesto a las ganancias en BigQuery).
  put('EBT', ebt);
  put('Net Income', ebt);
  // Lo que Finance sumó al P&L el 07/10/2026: el interés del préstamo, debajo.
  put('Interest expense', interest);
  put('Result after Interest', sub(ebt, interest));
  // ── Checks
  put('Opex total (Bonos anuales, taxes, etc)', monthly(data, (l) => opexOf(l)));
  put('Opex Total Sin Bono ni Taxes', add(cogs, inv, gya));
  put('Opex Total con Bono sin Taxes', add(cogs, inv, gya, bonos));
  put('Cogs con Taxes', add(cogs, taxes));
  // Regla de Finance (08/10/2026): Payroll = payroll de base_opex en Cogs + G&A + Investment;
  // Annual bonus = solo el concepto "Others" (los Bonos anuales del P&L). La planilla repartía
  // el mismo total distinto (ene-26: 749,6 + 129,7 vs 860,0 + 19,3).
  const payroll = total(sumBy(actualOpex.filter((r) => r.kind === 'payroll' && PAYROLL_LINES.includes(r.line)), c, () => 'x', (r) => r.usd));
  put('Payroll', payroll);
  put('Others (Annual bonus)', bonos);
  put('Non Payroll', nonPayroll);
  put('Existencial (G&A)', existencialGya);
  put('Non Payroll Excl Existencial', sub(nonPayroll, existencialGya));
  ratio('GM w/o taxes', sub(rev, cogs), rev);
  // ── Ratios sobre revenue
  ratio('% Direct Cost / Revenue', directT, rev);
  ratio('% Indirect Cost / Revenue', indirectT, rev);
  ratio('% Taxes / Revenue', taxes, rev);
  ratio('% Investment / Revenue', inv, rev);
  ratio('% G&A / Revenue', gya, rev);
  ratio('% EBITDA / Revenue', ebitda, rev);
  ratio('Gross Margin Total w/o Taxes', gp, rev);
  // ── Por solución
  for (const s of GM_SOLUTIONS) {
    const r = revBySolution.get(s) ?? zeros(c);
    const d = direct.get(s) ?? zeros(c);
    const ind = indirect.get(s) ?? zeros(c);
    ratio(`Gross Margin ${s} - Direct only`, sub(r, d), r);
    ratio(`Gross Margin ${s} - Direct+Indirect`, sub(sub(r, d), ind), r);
    put(`Indirect Cost Total - ${s}`, ind);
    ratio(`Weight Indirect Cost / Revenue - ${s}`, ind, r);
    ratio(`Weight Direct Cost / Revenue - ${s}`, d, r);
  }
  return out;
}

/** Financial Performance (Company Metrics) del año del dataset, con los nombres de la planilla. */
export function financialRowsFromPnl(data: PnlDashboardData): Map<string, CmSeries> {
  const c = data.closedMonth;
  const out = new Map<string, CmSeries>();
  const rev = monthly(data, (l) => l.revenue);
  const taxes = monthly(data, (l) => l.taxes);
  const cogs = monthly(data, (l) => l.cogs);
  const inv = monthly(data, (l) => l.investment);
  const gya = monthly(data, (l) => l.gya);
  const bonos = monthly(data, (l) => l.bonos);
  const gm = monthly(data, (l) => grossProfitOf(l));
  const ebitda = monthly(data, (l) => ebitdaOf(l));
  const plan = data.budget.length ? Array.from({ length: 12 }, (_, i) => K(ebitdaOf(data.budget[i]))) : null;
  const planBonos = data.budget.length ? Array.from({ length: 12 }, (_, i) => K(data.budget[i].bonos)) : null;
  out.set('Net Revenue', seriesFrom(rev, 'sum'));
  out.set('Taxes', seriesFrom(taxes, 'sum'));
  out.set('Cogs', seriesFrom(cogs, 'sum'));
  out.set('Gross Margin', seriesFrom(gm, 'sum'));
  out.set('Gross Margin %', ratioSeries(gm, rev));
  out.set('Investments', seriesFrom(inv, 'sum'));
  out.set('Investment as % Revenue', ratioSeries(inv, rev));
  out.set('G&A', seriesFrom(gya, 'sum'));
  out.set('G&A as % Revenue', ratioSeries(gya, rev));
  out.set('Bonos anuales', seriesFrom(bonos, 'sum'));
  out.set('EBITDA', seriesFrom(ebitda, 'sum'));
  out.set('EBITDA as % Revenue', ratioSeries(ebitda, rev));
  out.set('EBITDA Adj', seriesFrom(ebitda, 'sum'));
  if (plan && planBonos) {
    out.set('Ebitda - Plan', seriesFrom(plan, 'sum'));
    out.set('Ebitda - Plan pre bonos', seriesFrom(add(plan, planBonos), 'sum'));
    out.set('Actual vs Plan', seriesFrom(Array.from({ length: 12 }, (_, i) => (i + 1 <= c && ebitda[i] != null ? (ebitda[i] as number) - plan[i] : null)), 'sum'));
  }
  return out;
}

/**
 * Revenue Performance del año del dataset: target = revenue del presupuesto,
 * attainment contra el target del período completo (como la planilla), y
 * crecimientos contra el Net Revenue del año anterior (de la planilla o en vivo).
 */
export function revenueRowsFromPnl(data: PnlDashboardData, prevNetRevenue: CmSeries | null): Map<string, CmSeries> {
  const out = new Map<string, CmSeries>();
  const rev = monthly(data, (l) => l.revenue);
  const cur = seriesFrom(rev, 'sum');
  if (data.budget.length) {
    const target = Array.from({ length: 12 }, (_, i) => K(data.budget[i].revenue));
    out.set('Target: Net Revenue', seriesFrom(target, 'sum'));
    out.set('Attainment vs Target', ratioSeries(rev, target, { denAll: true }));
  }
  if (prevNetRevenue) {
    out.set('Net Revenue YoY Growth', growthSeries(cur, prevNetRevenue));
    // MoM: enero contra diciembre del año anterior; trimestre contra el trimestre anterior.
    const prevMonth = [prevNetRevenue.months[11], ...cur.months.slice(0, 11)];
    const prevQuarter = [prevNetRevenue.quarters[3], ...cur.quarters.slice(0, 3)];
    out.set('Net Revenue MoM Growth', growthSeries(cur, { months: prevMonth, quarters: prevQuarter, fy: null }));
  }
  return out;
}

/* ───────────────────────── overlays: Flywheel ───────────────────────── */

export interface FlywheelMonthRow {
  year: number;
  month: number;
  [col: string]: number;
}

/** Métrica de la planilla ← columna de Flywheel_Client_KPIs, factor y regla de agregado. */
export const FLYWHEEL_MAP: ReadonlyArray<readonly [string, string, number, CmAgg]> = [
  ['Quantity clients (By Holding)', 'quantity_clients', 1, 'last'],
  ['New Clients Active (By Holding)', 'new_clients_active', 1, 'sum'],
  ['Dormants (By Holding)', 'dormants', 1, 'sum'],
  ['Churns (By Holding)', 'churns', 1, 'sum'],
  ['Reactivations (By Holding)', 'reactivations', 1, 'sum'],
  ['Churned Revenue (K USD)', 'churned_revenue', 0.001, 'sum'],
  ['Revenue churn (%)', 'revenue_churn_pct', 0.01, 'last'],
  ['Churns/Active (Logos)', 'churns_over_active_pct', 0.01, 'last'],
  ['Top Client %', 'top1_holding_revenue_pct', 0.01, 'last'],
  ['Top 5 (%)', 'top5_holdings_revenue_pct', 0.01, 'last'],
  ['Top 10 (%)', 'top10_holdings_revenue_pct', 0.01, 'last'],
  ['Average Revenue per Client (K USD)', 'avg_revenue_per_holding', 0.001, 'last'],
  ['Mediana (Revenue per Client) (K USD)', 'median_revenue_per_holding', 0.001, 'last'],
  ['Whopper (# clientes)', 'whopper_clients', 1, 'last'],
  ['Key (# clientes)', 'key_clients', 1, 'last'],
  ['Growth (# clientes)', 'growth_clients', 1, 'last'],
  ['Opportunity (# clientes)', 'opportunity_clients', 1, 'last'],
  ['Holdings con >1 Solution', 'multi_solution_holdings', 1, 'last'],
  ['% Holdings con >1 Solution', 'multi_solution_holdings_pct', 0.01, 'last'],
];

/** Filas de Flywheel por año desde la vista mensual (+ NDR de compañía si viene). */
export function flywheelRows(
  rows: FlywheelMonthRow[],
  ndr: Array<{ year: number; month: number; ndr: number | null; ndrActive: number | null }>
): Map<string, Map<string, CmSeries>> {
  const out = new Map<string, Map<string, CmSeries>>();
  const years = [...new Set([...rows.map((r) => r.year), ...ndr.map((r) => r.year)])];
  for (const y of years) {
    const ys = new Map<string, CmSeries>();
    const inYear = rows.filter((r) => r.year === y);
    if (inYear.length) {
      for (const [metric, col, f, how] of FLYWHEEL_MAP) {
        const months = Array.from({ length: 12 }, (_, i) => {
          const r = inYear.find((x) => x.month === i + 1);
          const v = r?.[col];
          return typeof v === 'number' && Number.isFinite(v) ? v * f : null;
        });
        ys.set(metric, seriesFrom(months, how));
      }
    }
    const nd = ndr.filter((r) => r.year === y);
    if (nd.length) {
      const pick = (k: 'ndr' | 'ndrActive') => Array.from({ length: 12 }, (_, i) => nd.find((x) => x.month === i + 1)?.[k] ?? null);
      ys.set('Net Dollar Retention', seriesFrom(pick('ndr'), 'last'));
      ys.set('Net Dollar Retention - Still Active', seriesFrom(pick('ndrActive'), 'last'));
    }
    out.set(String(y), ys);
  }
  return out;
}

/* ───────────────────────── overlays: People ───────────────────────── */

export interface HeadcountMonth {
  month: number;
  total: number;
  direct: number;
  indirect: number;
  tech: number;
}

/**
 * People del año: headcount de P_L.headcount solo hasta el mes cerrado (`closed`; FP&A
 * 16/10/2026: la hoja "HC <año>" trae forecast hasta diciembre y eso NO se muestra, igual que
 * en prácticas) y los ratios contra el revenue Actual de Capacity_Final (solo meses con Actual
 * cerrado). Ratios trimestrales/FY = promedio de los meses.
 */
export function peopleRows(hc: HeadcountMonth[], revenueK: Array<number | null>, closed = 12): Map<string, CmSeries> {
  const out = new Map<string, CmSeries>();
  const at = (f: (h: HeadcountMonth) => number) => Array.from({ length: 12 }, (_, i) => {
    const h = i + 1 <= closed ? hc.find((x) => x.month === i + 1) : undefined;
    return h ? f(h) : null;
  });
  const total = at((h) => h.total);
  const direct = at((h) => h.direct);
  const indirect = at((h) => h.indirect);
  const tech = at((h) => h.tech);
  const cogsHc = add(direct, indirect);
  const sinTech = sub(cogsHc, tech);
  const directSinTech = sub(direct, tech);
  const div = (n: Array<number | null>, d: Array<number | null>) => n.map((v, i) => (v != null && d[i] ? v / (d[i] as number) : null));
  out.set('Headcount Winclap', seriesFrom(total, 'last'));
  out.set('Headcount Direct Cogs', seriesFrom(direct, 'last'));
  out.set('Headcount Indirect Cogs', seriesFrom(indirect, 'last'));
  out.set('Headcount Direct Cogs - Tech', seriesFrom(tech, 'last'));
  out.set('Ftes Cogs (Sin Tech, Direct+Indirect)', seriesFrom(sinTech, 'last'));
  out.set('Revenue (base ratios People)', seriesFrom(revenueK, 'sum'));
  out.set('Net Revenue / Headcount Cogs', seriesFrom(div(revenueK, cogsHc), 'mean'));
  out.set('Net Revenue / Headcount Direct Cogs', seriesFrom(div(revenueK, direct), 'mean'));
  out.set('Net Revenue / Headcount Direct Cogs (sin Tech)', seriesFrom(div(revenueK, directSinTech), 'mean'));
  return out;
}

/* ───────────────────────── aplicar overlays ───────────────────────── */

/**
 * Pisa las filas de una sub-pestaña con lo que salió en vivo para un año. Las
 * filas que la planilla no tenía se agregan al final (un centro de costo nuevo).
 * `families`: prefijos cubiertos por el overlay; una fila de esas familias que
 * no salió en vivo queda vacía para ese año (no con el número viejo).
 */
export function applyOverlay(rows: CmRow[], year: string, live: Map<string, CmSeries>, families: string[] = []): CmRow[] {
  const empty = seriesFrom([], 'sum');
  const seen = new Set<string>();
  const out = rows.map((r) => {
    const s = live.get(r.metric);
    if (s) {
      seen.add(r.metric);
      return { ...r, data: { ...r.data, [year]: s }, sources: { ...r.sources, [year]: 'live' as const } };
    }
    if (families.some((f) => r.metric.startsWith(f)) && r.data[year]) {
      return { ...r, data: { ...r.data, [year]: empty }, sources: { ...r.sources, [year]: 'live' as const } };
    }
    return r;
  });
  for (const [metric, s] of live) {
    if (seen.has(metric)) continue;
    out.push({ metric, format: formatOf(metric), data: { [year]: s }, sources: { [year]: 'live' } });
  }
  return out;
}

// ─── Practices y Segment desde BigQuery (spec de FP&A, 08/10/2026) ──────────────
//
// Las consultas viven en backend/src/lib/company-metrics-data.ts; acá se arman las
// series. Convenciones de la spec (§6): flujos suman, stocks toman el último valor,
// ratios = cociente de los agregados; Top Client / Top 5 / promedio / mediana / NDR /
// NPS vienen calculados en SQL a nivel mes, trimestre y año (no se derivan de los meses).
// Sin fila = null; una fila con 0 = 0.

export const CM_PRACTICES = [
  { key: 'pml', name: 'Paid Media Lab' },
  { key: 'content', name: 'Content' },
  { key: 'oml', name: 'Owned Media Lab' },
  { key: 'tds', name: 'Tech & Data Studio' },
  { key: 'dfc', name: 'Data for Content' },
] as const;

export const CM_SEGMENTS = [
  { key: 'rola', segment: 'ROLA', nps: 'ROLA' },
  { key: 'whoppers', segment: 'Whoppers', nps: 'WHOPPERS' },
  { key: 'growth_transformation', segment: 'Growth Transformation', nps: 'GROWTH TRANSFORMATION' },
  { key: 'andinos', segment: 'Andinos', nps: 'ANDINOS' },
  { key: 'mexico', segment: 'Mexico', nps: 'MEXICO' },
  { key: 'usa', segment: 'USA', nps: 'USA' },
  { key: 'brazil', segment: 'Brazil', nps: 'BRAZIL' },
  /** Segment = 'Partnerships' AND PracticeName = 'Content'; su NPS sale de Partnerships_vw_nps_responses. */
  { key: 'partnerships_content', segment: 'Partnerships', nps: null },
] as const;

/** Una fila mensual de una consulta: año, mes, clave (práctica o segmento) y sus valores en USD o cantidades. */
export interface CmKeyMonthRow { year: number; month: number; key: string; [field: string]: number | string | null }
/** Una fila calculada en SQL por granularidad: M (p = mes), Q (p = trimestre) o Y (p = 0). */
export interface CmKeyPeriodRow { year: number; g: 'M' | 'Q' | 'Y'; p: number; key: string; [field: string]: number | string | null }

const nz = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : v == null ? null : Number.isFinite(Number(v)) ? Number(v) : null);

/** 12 meses de un campo para una clave y año (K USD si `k`). Sin fila = null. */
export function monthsOf(rows: CmKeyMonthRow[], year: number, key: string, field: string, opts: { k?: boolean } = {}): Array<number | null> {
  const out = Array.from({ length: 12 }, () => null as number | null);
  for (const r of rows) {
    if (r.year !== year || r.key !== key || r.month < 1 || r.month > 12) continue;
    const v = nz(r[field]);
    if (v === null) continue;
    out[r.month - 1] = (out[r.month - 1] ?? 0) + (opts.k ? v / 1000 : v);
  }
  return out;
}

/** Serie armada desde filas ya agregadas en SQL por mes / trimestre / año. */
export function seriesFromPeriods(rows: CmKeyPeriodRow[], year: number, key: string, field: string, opts: { k?: boolean } = {}): CmSeries {
  const f = (v: unknown) => { const x = nz(v); return x === null ? null : opts.k ? x / 1000 : x; };
  const mine = rows.filter((r) => r.year === year && r.key === key);
  const at = (g: 'M' | 'Q' | 'Y', p: number) => f(mine.find((r) => r.g === g && r.p === p)?.[field]);
  return {
    months: Array.from({ length: 12 }, (_, i) => at('M', i + 1)),
    quarters: [1, 2, 3, 4].map((q) => at('Q', q)),
    fy: at('Y', 0),
  };
}

/** NPS trimestral: el valor del trimestre se repite en sus 3 meses (§5); el FY es el cálculo anual directo. */
export function npsSeries(rows: CmKeyPeriodRow[], year: number, key: string): CmSeries {
  const mine = rows.filter((r) => r.year === year && r.key === key);
  const q = [1, 2, 3, 4].map((p) => nz(mine.find((r) => r.g === 'Q' && r.p === p)?.pct));
  return { months: Array.from({ length: 12 }, (_, i) => q[Math.floor(i / 3)]), quarters: q, fy: nz(mine.find((r) => r.g === 'Y')?.pct) };
}

/** Revenue / Headcount: el headcount es stock (último valor del período), el revenue flujo (§6). */
export function revPerHeadcount(rev: Array<number | null>, hc: Array<number | null>): CmSeries {
  const revS = seriesFrom(rev, 'sum');
  const hcS = seriesFrom(hc, 'last');
  const div = (a: number | null, b: number | null) => (a != null && b ? a / b : null);
  return {
    months: rev.map((v, i) => div(v, hc[i] ?? null)),
    quarters: revS.quarters.map((v, i) => div(v, hcS.quarters[i])),
    fy: div(revS.fy, hcS.fy),
  };
}

export interface CmPracticeInput {
  /** Último año/mes con Actual cerrado (los meses abiertos ya vienen afuera de las consultas). */
  year: number;
  /** Mensual por práctica: rev, plan, planH2, clients, rebates, partnerships (USD salvo clients); key 'total' = todas las prácticas. */
  monthly: CmKeyMonthRow[];
  /**
   * Las fuentes que no son el revenue mensual pueden venir en null = la consulta falló: sus
   * métricas NO se setean y la fila sigue con la planilla (code-reviewer 08/10/2026: con []
   * salían series vacías marcadas 'live' que pisaban la planilla con "—").
   */
  /** SQL por M/Q/Y: top1, top5, avg, median (USD) — PML y Content. */
  concentration: CmKeyPeriodRow[] | null;
  /** SQL por M/Q/Y: ndr, ndrActive — por práctica y 'content_ex' (Content sin Partnerships). */
  ndr: CmKeyPeriodRow[] | null;
  /** Año corriente, mensual: cogs (USD) y headcount. */
  cogs: CmKeyMonthRow[] | null;
  headcount: CmKeyMonthRow[] | null;
  /** Satisfacción por práctica (SQL por Q/Y): pct. */
  nps: CmKeyPeriodRow[] | null;
}

/**
 * Target = EMPALME Plan (ene–jun) + Plan H2 (jul–dic). Decisión de FP&A (confirmada el 16/10/2026
 * para prácticas y segmentos): de julio a diciembre vale el H2; nunca SourceType = 'Target'.
 */
export function spliceTarget(plan: Array<number | null>, planH2: Array<number | null>): Array<number | null> {
  return plan.map((v, i) => (i < 6 ? v : planH2[i] ?? null));
}

/** Desde qué año el Target se empalma con el Plan H2. Antes (2025) vale el Plan original los 12 meses (FP&A 16/10/2026). */
export const PLAN_H2_FROM_YEAR = 2026;

/** Target del año: empalme Plan + Plan H2 desde `PLAN_H2_FROM_YEAR`; en los años anteriores, el Plan los 12 meses. */
export function targetSeries(year: number, plan: Array<number | null>, planH2: Array<number | null>): Array<number | null> {
  return year >= PLAN_H2_FROM_YEAR ? spliceTarget(plan, planH2) : plan.map((v) => v ?? null);
}

/** Por práctica → año → métrica → serie. Solo lo que tiene fuente en vivo; el resto sigue con la planilla. */
export function practiceSeries(input: CmPracticeInput): Map<string, Map<string, Map<string, CmSeries>>> {
  const out = new Map<string, Map<string, Map<string, CmSeries>>>();
  const years = [...new Set(input.monthly.map((r) => r.year))].filter((y) => y >= 2024 && y <= input.year).sort();
  for (const { key } of CM_PRACTICES) {
    const byYear = new Map<string, Map<string, CmSeries>>();
    for (const y of years) {
      const m = new Map<string, CmSeries>();
      const rev = monthsOf(input.monthly, y, key, 'rev', { k: true });
      // Denominador: revenue de TODAS las prácticas de Capacity (filas con key 'total').
      const totalRev = monthsOf(input.monthly, y, 'total', 'rev', { k: true });
      const target = targetSeries(y, monthsOf(input.monthly, y, key, 'plan', { k: true }), monthsOf(input.monthly, y, key, 'planH2', { k: true }));
      m.set('Revenue per Practices', seriesFrom(rev, 'sum'));
      m.set('Share% of total Revenue', ratioSeries(rev, totalRev));
      m.set('Net Revenue - Target', seriesFrom(target, 'sum'));
      m.set('Attainment vs Target', ratioSeries(rev, target, { denAll: true }));
      m.set('Quantity clients', seriesFrom(monthsOf(input.monthly, y, key, 'clients'), 'last'));
      if (years.includes(y - 1)) {
        const prev = seriesFrom(monthsOf(input.monthly, y - 1, key, 'rev', { k: true }), 'sum');
        m.set('Net Revenue previous year', prev);
        m.set('Net Revenue YoY Growth', growthSeries(seriesFrom(rev, 'sum'), prev, { fy: true }));
      }
      if (y >= 2025 && input.ndr) {
        m.set('Net Dollar Retention', seriesFromPeriods(input.ndr, y, key, 'ndr'));
        m.set('Net Dollar Retention - Still Active', seriesFromPeriods(input.ndr, y, key, 'ndrActive'));
      }
      if (key === 'pml') {
        const reb = monthsOf(input.monthly, y, key, 'rebates', { k: true });
        m.set('Rebates', seriesFrom(reb, 'sum'));
        m.set('Share% Rebates', ratioSeries(reb, rev));
        if (input.concentration) {
          m.set('Top Client %', seriesFromPeriods(input.concentration, y, key, 'top1'));
          m.set('Top 5 (%)', seriesFromPeriods(input.concentration, y, key, 'top5'));
        }
      }
      if ((key === 'pml' || key === 'content') && input.concentration) {
        m.set('Average Revenue per Client', seriesFromPeriods(input.concentration, y, key, 'avg', { k: true }));
        m.set('Mediana', seriesFromPeriods(input.concentration, y, key, 'median', { k: true }));
      }
      if (key === 'content') {
        m.set('Revenue Partnerships', seriesFrom(monthsOf(input.monthly, y, key, 'partnerships', { k: true }), 'sum'));
        if (y >= 2025 && input.ndr?.some((r) => r.key === 'content_ex')) {
          m.set('Net Dollar Retention exc. Partnerships', seriesFromPeriods(input.ndr, y, 'content_ex', 'ndr'));
          m.set('Net Dollar Retention - Still Active exc. Partnerships', seriesFromPeriods(input.ndr, y, 'content_ex', 'ndrActive'));
        }
      }
      // Satisfacción por práctica (Client_vw_solution_ratings, score >= 6 de 7). Data for Content no tiene (§5).
      if (key !== 'dfc' && input.nps?.some((r) => r.year === y && r.key === key)) m.set('NPS - % Of clients Satisfied', npsSeries(input.nps, y, key));
      // Cogs y headcount solo existen para el año corriente (base_opex / P_L.headcount, §7).
      if (y === input.year) {
        const cogs = monthsOf(input.cogs ?? [], y, key, 'usd', { k: true });
        if (cogs.some((v) => v != null)) {
          // Solo en meses con Cogs: un mes con revenue y sin cierre de costos daba margen 100 %.
          const gp = rev.map((v, i) => (v == null || cogs[i] == null ? null : v - (cogs[i] as number)));
          m.set('Cogs', seriesFrom(cogs, 'sum'));
          m.set('Gross Profit', seriesFrom(gp, 'sum'));
          m.set('Gross Margin as % Revenue', ratioSeries(gp, rev));
        }
        const hc = monthsOf(input.headcount ?? [], y, key, 'hc');
        if (hc.some((v) => v != null)) {
          m.set('Headcount', seriesFrom(hc, 'last'));
          m.set('Rev per Headcount', revPerHeadcount(rev, hc));
        }
      }
      byYear.set(String(y), m);
    }
    out.set(key, byYear);
  }
  return out;
}

export interface CmSegmentInput {
  year: number;
  /** Mensual por segmento: rev, ssg, nb (Actual), plan, planH2 (USD). */
  monthly: CmKeyMonthRow[];
  /** Net Revenue de la compañía (K USD) por año: el denominador del Share% (§4), no la suma de los segmentos. */
  companyNetRevenue: Record<string, Array<number | null>>;
  /** NPS promoter % por segmento (SQL por Q/Y): pct. null = la consulta falló (no se setea). */
  nps: CmKeyPeriodRow[] | null;
}

export function segmentSeries(input: CmSegmentInput): Map<string, Map<string, Map<string, CmSeries>>> {
  const out = new Map<string, Map<string, Map<string, CmSeries>>>();
  const years = [...new Set(input.monthly.map((r) => r.year))].filter((y) => y >= 2024 && y <= input.year).sort();
  for (const { key } of CM_SEGMENTS) {
    const byYear = new Map<string, Map<string, CmSeries>>();
    for (const y of years) {
      const m = new Map<string, CmSeries>();
      const rev = monthsOf(input.monthly, y, key, 'rev', { k: true });
      const plan = monthsOf(input.monthly, y, key, 'plan', { k: true });
      const planH2 = monthsOf(input.monthly, y, key, 'planH2', { k: true });
      // Target = empalme Plan (ene–jun) + Plan H2 (jul–dic) desde 2026; Plan = el original los 12 meses (§4).
      const target = targetSeries(y, plan, planH2);
      m.set('Revenue', seriesFrom(rev, 'sum'));
      m.set('Revenue SSG', seriesFrom(monthsOf(input.monthly, y, key, 'ssg', { k: true }), 'sum'));
      m.set('Revenue NB', seriesFrom(monthsOf(input.monthly, y, key, 'nb', { k: true }), 'sum'));
      m.set('Share% of total Revenue', ratioSeries(rev, input.companyNetRevenue[String(y)] ?? []));
      if (years.includes(y - 1)) {
        const prev = seriesFrom(monthsOf(input.monthly, y - 1, key, 'rev', { k: true }), 'sum');
        m.set('Net Revenue previous year', prev);
        m.set('Net Revenue YoY Growth', growthSeries(seriesFrom(rev, 'sum'), prev, { fy: true }));
      }
      m.set('Net Revenue - Target', seriesFrom(target, 'sum'));
      m.set('Attainment vs Target', ratioSeries(rev, target, { denAll: true }));
      m.set('Net Revenue - Plan', seriesFrom(plan, 'sum'));
      m.set('Attainment vs Plan', ratioSeries(rev, plan, { denAll: true }));
      // Solo donde la vista tiene respuestas: un segmento-año sin filas (USA 2026) pisaba la planilla con "—".
      if (input.nps?.some((r) => r.year === y && r.key === key)) m.set('NPS promoter % of answers (Q)', npsSeries(input.nps, y, key));
      byYear.set(String(y), m);
    }
    out.set(key, byYear);
  }
  return out;
}
